import { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../../../contexts/user-context/user-context";
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  Paper,
  Divider,
  Stack,
} from "@mui/material";
import { BranchType } from "../../../interfaces/delivery-company/branches";
import Grid2 from "@mui/material/Unstable_Grid2";
import { BranchSelector } from "../../common/selectors/branch-selector";
import axios from "axios";
import { API_ENDPOINT } from "../../../configurations/global.config";
import { PaginatedResponse, usePaginatedData } from "../../../hooks/pagination";
import { OrderType } from "../../../interfaces/delivery-company/orders";
import GenericTable from "../../common/generic-table/generic-table";
import { MerchantWithIcon } from "../../common/styles/merchant-with-icon";
import { CustomerWithIcon } from "../../common/styles/customer-with-icon";
import { showWorkflow } from "../../common/styles/workflow-display";
import { displayLocation } from "../../../utils/locations";
import { useImmer } from "use-immer";
import { SelectionBasket } from "./selection-basket";
import { InternalTransferTasksPanel } from "./tasks";
import { useTabPersistedState } from "../../common/tabPersistedState/tab-persisted-state";
import { useSelectedBranch } from "../../../contexts/selected-branch-context";
import FilterChips from "../../common/filter-chips/filter-chips";

type TransferType = "Inbound" | "Outbound"

export const InternalTransfersPage = () => {
  const { config } = useContext(AuthContext);

  const [persistedBranchId, setPersistedBranchId] = useTabPersistedState(
    "internal-transfers-selected-branch-id",
    null
  );

  const {selectedBranch: selectedBranchFromContext} = useSelectedBranch();
  const [selectedBranch, setSelectedBranch] = useState<BranchType | null>(null);
  const [transferType, setTransferType] = useTabPersistedState("internal-transfer-type", "Inbound");

  // Stats for both inbound and outbound (orders and tasks)
  const [transferStats, setTransferStats] = useState<{
    inbound: {
      orders: Map<number | string, number>;
      tasks: Map<number | string, number>;
    };
    outbound: {
      orders: Map<number | string, number>;
      tasks: Map<number | string, number>;
    };
  } | null>(null);

  const [selectedOrders, setSelectedOrders] =
    useImmer<Map<string, OrderType>>(new Map());

  // Track refresh trigger to force refetch even when on same page
  const [ordersRefreshTrigger, setOrdersRefreshTrigger] = useState(0);

  // Handle branch selection with persistence (for Outbound - target branch)
  const handleBranchSelect = (branch: BranchType) => {
    setSelectedBranch(branch);
    setPersistedBranchId(String(branch.id));
  };

  // Only fetch orders for Outbound transfers
  const { data, loading } = usePaginatedData<OrderType>(
    async (page, pageSize) => {
      if (!selectedBranch || transferType !== "Outbound")
        return {
          count: 0,
          results: [] as OrderType[],
        } as PaginatedResponse<OrderType>;

      const endpoint = `${API_ENDPOINT}/v1/orders/?awaiting_transfer_to=${
        transferType == "Outbound" ?  selectedBranch.id : selectedBranchFromContext?.id
      }&page=${page + 1}&page_size=${pageSize}&awaiting_transfer_from=${
        transferType == "Inbound" ?  selectedBranch.id : selectedBranchFromContext?.id
      }`;
      return (
        await axios.get<PaginatedResponse<OrderType>>(endpoint, config)
      ).data;
    },
    [selectedBranch, ordersRefreshTrigger, transferType]
  );

  // Fetch branch stats function that can be called from multiple places
  const fetchBranchStats = useCallback(async () => {
    const { data } = await axios.get<{
      inbound: {
        orders: { branch: number; count: number }[];
        tasks: { branch: number; count: number }[];
      };
      outbound: {
        orders: { branch: number; count: number }[];
        tasks: { branch: number; count: number }[];
      };
    }>(`${API_ENDPOINT}/v1/internal-transfers/stats/`, config);
    
    setTransferStats({
      inbound: {
        orders: new Map(data.inbound.orders.map((item) => [item.branch, item.count])),
        tasks: new Map(data.inbound.tasks.map((item) => [item.branch, item.count])),
      },
      outbound: {
        orders: new Map(data.outbound.orders.map((item) => [item.branch, item.count])),
        tasks: new Map(data.outbound.tasks.map((item) => [item.branch, item.count])),
      },
    });
  }, [config]);

  useEffect(() => {
    fetchBranchStats();
  }, [fetchBranchStats]);

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: "center", mt: 1, mb: 2 }}
      >
        Internal Transfer
      </Typography>

      <Box mb={1}>
        <FilterChips
          options={["Inbound", "Outbound"]} 
          label={""} 
          centered
          selected={[transferType]} 
          onChange={(selected: TransferType[]) => setTransferType(selected[0] ?? transferType)}        
        />
      </Box>
      


      {/* Branch Selector at the top */}
      <Box justifyContent={"center"} width={"100%"} display={"flex"}>
        <BranchSelector
          key={(selectedBranchFromContext?.id ?? "") + transferType}
          selectedBranch={selectedBranch}
          onSelect={handleBranchSelect}
          selectFirstOnLoad={!persistedBranchId}
          defaultSelectedBranchID={persistedBranchId}
          filter={(b) =>  b.id != selectedBranchFromContext?.id}
          footer={(b, selected) =>
            transferStats == null ? (
              <CircularProgress size={20} />
            ) : (
              <Stack direction="column" spacing={0.25} sx={{ mt: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  color={selected ? "warning" : "default"}
                  label={`${transferType == "Outbound" ? (transferStats.outbound.orders.get(b.id) ?? "0") : (transferStats.inbound.orders.get(b.id) ?? "0")} orders awaiting`}
                  sx={{ fontSize: '0.65rem', height: 20}}
                />
                <Chip
                  size="small"
                  color={selected ? "info" : "default"}
                  label={`${transferType == "Outbound" ? (transferStats.outbound.tasks.get(b.id) ?? "0") : (transferStats.inbound.tasks.get(b.id) ?? "0")} active tasks`}
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              </Stack>
            )
          }
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid2 spacing={2} container>
        {/* LEFT SIDE: Orders table (only for Outbound) */}
        {transferType === "Outbound" && (
          <Grid2 xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" component="h6" sx={{ mb: 1 }}>
                {selectedBranch
                  ? `Orders to transfer to ${selectedBranch.name}`
                  : "Select a branch to view orders"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select orders from the table below and add them to transfer tasks
              </Typography>
            </Box>

            {/* Compact selection basket */}
            {selectedOrders.size > 0 && (
              <Box sx={{ mb: 2 }}>
                <SelectionBasket selectedOrders={selectedOrders} />
              </Box>
            )}

            <GenericTable
              items={data}
              selectedItems={selectedOrders}
              updateSelectedItems={setSelectedOrders}
              selectionEnabled
              isLoading={loading}
              fullWidth
              columns={[
                {
                  header: "Order ID",
                  display_body: (o) => <>{o.order_id}</>,
                  id: "id",
                },
                {
                  header: "Branch",
                  display_body: (o) => <>{o.branch?.name}</>,
                  id: "branch",
                },
                {
                  header: "Merchant",
                  display_body: (o) => (
                    <Box>
                      <MerchantWithIcon merchant={o.merchant} centered />
                      {o.merchant_location && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                          {displayLocation(o.merchant_location)}
                        </Typography>
                      )}
                    </Box>
                  ),
                  id: "merchant",
                },
                {
                  header: "Customer",
                  display_body: (o) => (
                    <Box>
                      <CustomerWithIcon customer={o.customer} centered />
                      {o.customer_location && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                          {displayLocation(o.customer_location)}
                        </Typography>
                      )}
                    </Box>
                  ),
                  id: "customer",
                },
                {
                  header: "Workflow",
                  display_body: (o) => (
                    <>
                      {showWorkflow(
                        o.workflow,
                        o.is_exchange,
                        o.exchange_for,
                        o.return_reason
                      )}
                    </>
                  ),
                  id: "workflow",
                },
              ]}
              getItemUID={(o) => o.order_id}
              sourceView={"internal-transfers-orders"}
            />
          </Grid2>
        )}

        {/* RIGHT SIDE: Tasks panel */}
        <Grid2 xs={12} md={transferType === "Inbound" ? 12 : 6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <InternalTransferTasksPanel
              selectedOrders={selectedOrders}
              selectedBranch={selectedBranch}
              transferType={transferType}
              currentBranch={selectedBranchFromContext}
              onSelectedBasketAddedToTask={()=>{
                setSelectedOrders(new Map); 
                // Trigger refresh by incrementing refreshTrigger - this forces a refetch
                // even if we're already on page 0
                setOrdersRefreshTrigger((prev) => prev + 1);
                // Refetch branch stats to update the order counts
                fetchBranchStats();
              }}
              onTaskDeleted={() => {
                // Refetch orders and branch stats when a task is deleted/cancelled
                setOrdersRefreshTrigger((prev) => prev + 1);
                fetchBranchStats();
              }}
              onOrdersRemovedFromTask={() => {
                // Refetch orders and branch stats when orders are removed from a task
                setOrdersRefreshTrigger((prev) => prev + 1);
                fetchBranchStats();
              }}
            />
          </Paper>
        </Grid2>
      </Grid2>
    </>
  );
};
